import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react'
import type { MouseEvent, KeyboardEvent, TouchEvent } from 'react'
import { useDrag } from 'react-dnd'
import i18n from 'i18next'
import { DeleteDateRowIcon as IconDeleteDateRow } from 'assets/icons/DeleteDateRowIcon'
import { v4 as uuid } from 'uuid'
import styled from '@emotion/styled'
import { AtlasTooltip } from 'components/atlas-components/atlas-tooltip/Tooltip'
import { SelectChip } from './StyledComponents'
import { ColorBaseSkyBlue } from 'assets/styles/variables'
import { AttendeeData } from './ThreeSectionAttendeeManager/attendeeUtils'

export const ItemType = 'USER_CHIP'

const ATTENDANCE_PARTIAL_TEXT = i18n.t('ATTENDANCE_PARTIAL')
const ATTENDANCE_FULL_TEXT = i18n.t('ATTENDANCE_FULL')
const REMOTE_TEXT = i18n.t('ATTENDANCE_REMOTE')
const IN_PERSON_TEXT = i18n.t('ATTENDANCE_IN_PERSON')

const TransparentSelectChip = styled(SelectChip)<{ isOver?: boolean; isDragging?: boolean }>`
    && {
        background-color: transparent;
        border: 1px solid #676767;
        border-radius: 16px;
        margin: 0 !important;
        &:hover {
            // background-color: rgba(0, 0, 0, 0.04);
            border: 1px solid #676767;
            border-radius: 16px;
        }
        &:focus {
            background-color: transparent;
            border: 1px solid #676767;
            outline: none !important;
            box-shadow: 0 0 0 2px ${ColorBaseSkyBlue} !important;
            border-radius: 16px;
        }
        &:focus-visible {
            outline: none !important;
            box-shadow: 0 0 0 2px ${ColorBaseSkyBlue} !important;
            border-radius: 16px;
        }
        & [class*='WithStyles'][class*='Chip'][class*='label'],
        & [class*='MuiChip-label'] {
            padding-left: 5px !important;
        }
    }
`

const ChipWrapper = styled.div`
    position: relative;
    display: inline-block;
`

const DropIndicator = styled.div<{ position: 'left' | 'right' }>`
    position: absolute;
    ${({ position }) => (position === 'left' ? 'left: -10px;' : 'right: -10px;')}
    top: 50%;
    transform: translateY(-50%);
    width: 2px;
    height: 28px;
    background-color: #1E1E1E;
    border-radius: 1px;
    pointer-events: none;
    z-index: 10;
`

type DragItem = {
    type: string
    id: string | number
    external_id?: string | number | null
    index: number
    sourceSection?: string
    name: string
    email: string | null
    isTimeLate?: boolean
    isLocationRemote?: boolean
}

type CollectedProps = {
    isDragging: boolean
}

type Props = {
    id: string | number
    name: string
    email: string | null
    index: number
    sourceSection?: string
    onMove: (dragIndex: number, hoverIndex: number) => void
    onRemove?: (index: number) => void
    icon?: React.ReactNode
    interactiveIcon?: React.ReactNode
    secondaryInteractiveIcon?: React.ReactNode
    totalChips?: number
    onMoveToNextSection?: (attendee: AttendeeData) => void
    onMoveToPreviousSection?: (attendee: AttendeeData) => void
    attendee?: AttendeeData
    isTimeLate?: boolean
    isLocationRemote?: boolean
    onTimeIconToggle?: () => void
    onLocationIconToggle?: () => void
    onFocusInput?: () => void
    readonlyForCreate?: boolean
    isOver?: boolean
    dropPosition?: 'left' | 'right' | null
}

type DisplayType = 'name' | 'email'

export const DraggableUserChip: React.FC<Props> = ({
    id,
    name,
    email,
    index,
    sourceSection,
    onMove,
    onRemove,
    icon,
    interactiveIcon,
    secondaryInteractiveIcon,
    totalChips,
    onMoveToNextSection,
    onMoveToPreviousSection,
    attendee,
    isTimeLate = false,
    isLocationRemote = false,
    onTimeIconToggle,
    onLocationIconToggle,
    onFocusInput,
    readonlyForCreate = false,
    isOver = false,
    dropPosition = null
}) => {
    const [displayType, setDisplayType] = useState<DisplayType>('name')
    const [renderKey, setRenderKey] = useState(0)
    const chipRef = useRef<HTMLDivElement>(null)
    const prevIsTimeLateRef = useRef<boolean | undefined>(isTimeLate)
    const prevIsLocationRemoteRef = useRef<boolean | undefined>(isLocationRemote)

    // Force re-render when isTimeLate or isLocationRemote props change
    // This fixes the iPad rendering issue where icon state doesn't update immediately
    useEffect(() => {
        if (
            prevIsTimeLateRef.current !== isTimeLate ||
            prevIsLocationRemoteRef.current !== isLocationRemote
        ) {
            prevIsTimeLateRef.current = isTimeLate
            prevIsLocationRemoteRef.current = isLocationRemote
            // Force re-render by updating renderKey
            setRenderKey((prev) => prev + 1)
        }
    }, [isTimeLate, isLocationRemote])

    const chipEmail = email || i18n.t('NO_EMAIL_PERSON_LABEL')
    const label = displayType === 'name' ? name : chipEmail

    const [{ isDragging }, drag, preview] = useDrag<DragItem, void, CollectedProps>({
        item: {
            type: ItemType,
            id,
            external_id: attendee?.external_id,
            index,
            sourceSection,
            name,
            email,
            isTimeLate,
            isLocationRemote
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging()
        }),
        canDrag: !readonlyForCreate
    })

    useEffect(() => {
        if (!isDragging) return

        let animationFrameId: number | null = null
        let lastMouseY = 0

        const scroll = () => {
            if (lastMouseY > 0) {
                const viewportHeight = window.innerHeight
                const threshold90 = viewportHeight * 0.9
                const threshold35 = viewportHeight * 0.35

                if (lastMouseY > threshold90) {
                    const scrollSpeed = 5

                    const allElements = document.querySelectorAll('*')

                    allElements.forEach((element) => {
                        const el = element as HTMLElement
                        if (el.scrollHeight > el.clientHeight + 5) {
                            el.scrollTop += scrollSpeed
                        }
                    })
                } else if (lastMouseY < threshold35) {
                    const scrollSpeed = -5

                    const allElements = document.querySelectorAll('*')

                    allElements.forEach((element) => {
                        const el = element as HTMLElement
                        if (el.scrollTop > 0) {
                            el.scrollTop += scrollSpeed
                        }
                    })
                }
            }

            animationFrameId = requestAnimationFrame(scroll)
        }

        const handleDragOver = (e: DragEvent) => {
            e.preventDefault()
            lastMouseY = e.clientY
        }

        const handleDrag = (e: DragEvent) => {
            if (e.clientY > 0) {
                lastMouseY = e.clientY
            }
        }

        const handleDragEnd = () => {
            // Drag ended
        }

        animationFrameId = requestAnimationFrame(scroll)

        document.addEventListener('dragover', handleDragOver, { passive: false })
        document.addEventListener('drag', handleDrag, { passive: false })
        document.addEventListener('dragend', handleDragEnd)

        return () => {
            document.removeEventListener('dragover', handleDragOver)
            document.removeEventListener('drag', handleDrag)
            document.removeEventListener('dragend', handleDragEnd)
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId)
            }
        }
    }, [isDragging])

    useEffect(() => {
        if (isDragging) {
            document.body.style.cursor = 'grabbing'
        }
        return () => {
            document.body.style.cursor = ''
        }
    }, [isDragging])

    const opacity = 1

    const handleClick = useCallback(
        (e?: any) => {
            if (readonlyForCreate) return
            if (e) {
                e.preventDefault()
                e.stopPropagation()
            }
            setDisplayType((prev) => (prev === 'name' ? 'email' : 'name'))
            if (e?.currentTarget) {
                ;(e.currentTarget as HTMLElement)?.blur()
            }
            onFocusInput?.()
        },
        [readonlyForCreate, onFocusInput]
    )

    const handleTimeIconClick = useCallback(
        (e: MouseEvent<HTMLElement> | TouchEvent<HTMLElement>) => {
            if (readonlyForCreate) return
            e.preventDefault()
            e.stopPropagation()
            onTimeIconToggle?.()
            ;(e.currentTarget as HTMLElement)?.blur()
            onFocusInput?.()
        },
        [readonlyForCreate, onTimeIconToggle, onFocusInput]
    )

    const handleLocationIconClick = useCallback(
        (e: MouseEvent<HTMLElement> | TouchEvent<HTMLElement>) => {
            if (readonlyForCreate) return
            e.preventDefault()
            e.stopPropagation()
            onLocationIconToggle?.()
            ;(e.currentTarget as HTMLElement)?.blur()
            onFocusInput?.()
        },
        [readonlyForCreate, onLocationIconToggle, onFocusInput]
    )

    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLDivElement>) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleClick()
            }
        },
        [handleClick]
    )

    const handleLabelTouchEnd = useCallback(
        (e: TouchEvent<HTMLElement>) => {
            if (readonlyForCreate) return
            e.preventDefault()
            e.stopPropagation()
            handleClick(e)
        },
        [readonlyForCreate, handleClick]
    )

    const handleTimeIconTouchEnd = useCallback(
        (e: TouchEvent<HTMLElement>) => {
            if (readonlyForCreate) return
            e.preventDefault()
            e.stopPropagation()
            handleTimeIconClick(e)
        },
        [readonlyForCreate, handleTimeIconClick]
    )

    const handleLocationIconTouchEnd = useCallback(
        (e: TouchEvent<HTMLElement>) => {
            if (readonlyForCreate) return
            e.preventDefault()
            e.stopPropagation()
            handleLocationIconClick(e)
        },
        [readonlyForCreate, handleLocationIconClick]
    )

    const validId = useMemo(() => id || `fallback_${name}`, [id, name])

    const createFormattedAttendee = useCallback(
        (): AttendeeData => ({
            id: validId,
            text: attendee?.text || attendee?.name || name,
            name: attendee?.name || attendee?.text || name,
            email: attendee?.email || email,
            isTimeLate: isTimeLate,
            isLocationRemote: isLocationRemote
        }),
        [validId, attendee, name, email, isTimeLate, isLocationRemote]
    )

    const handleChipContainerKeyDown = useCallback(
        (e: KeyboardEvent<HTMLDivElement>) => {
            if (readonlyForCreate) return

            if (e.key === 'ArrowLeft') {
                e.preventDefault()
                e.stopPropagation()
                if (index === 0 && attendee && onMoveToPreviousSection) {
                    onMoveToPreviousSection(createFormattedAttendee())
                } else if (index > 0) {
                    onMove(index, index - 1)
                }
            } else if (e.key === 'ArrowRight') {
                e.preventDefault()
                e.stopPropagation()
                if (
                    totalChips !== undefined &&
                    index === totalChips - 1 &&
                    attendee &&
                    onMoveToNextSection
                ) {
                    onMoveToNextSection(createFormattedAttendee())
                } else if (totalChips !== undefined && index < totalChips - 1) {
                    onMove(index, index + 1)
                }
            } else if (e.key === 'Delete' && onRemove) {
                e.preventDefault()
                onRemove(index)
            }
        },
        [
            readonlyForCreate,
            index,
            attendee,
            onMoveToPreviousSection,
            onMoveToNextSection,
            onMove,
            totalChips,
            onRemove,
            createFormattedAttendee
        ]
    )

    const createKeyboardHandler = useCallback(
        (handler: () => void) => (e: KeyboardEvent<HTMLDivElement>) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handler()
                onFocusInput?.()
            }
        },
        [onFocusInput]
    )

    const handleTimeIconKeyDown = useMemo(() => createKeyboardHandler(() => onTimeIconToggle?.()), [
        createKeyboardHandler,
        onTimeIconToggle
    ])

    const handleLocationIconKeyDown = useMemo(
        () => createKeyboardHandler(() => onLocationIconToggle?.()),
        [createKeyboardHandler, onLocationIconToggle]
    )

    const handleDeleteKeyDown = useMemo(() => createKeyboardHandler(() => onRemove?.(index)), [
        createKeyboardHandler,
        onRemove,
        index
    ])

    const handleDeleteClick = useCallback(
        (e: MouseEvent<HTMLElement> | TouchEvent<HTMLElement>) => {
            if (readonlyForCreate) return
            e.preventDefault()
            e.stopPropagation()
            onRemove?.(index)
        },
        [readonlyForCreate, onRemove, index]
    )

    const handleDeleteTouchEnd = useCallback(
        (e: TouchEvent<HTMLElement>) => {
            if (readonlyForCreate) return
            e.preventDefault()
            e.stopPropagation()
            onRemove?.(index)
        },
        [readonlyForCreate, onRemove, index]
    )

    const key = useMemo(() => uuid(), [])

    const dragHandleRef = useCallback(
        (node: HTMLSpanElement | null) => {
            if (!readonlyForCreate) {
                drag(node)
            }
        },
        [drag, readonlyForCreate]
    )

    const previewRef = useCallback(
        (node: HTMLDivElement | null) => {
            ;(chipRef as React.MutableRefObject<HTMLDivElement | null>).current = node
            if (!readonlyForCreate) {
                preview(node)
            }
        },
        [preview, readonlyForCreate]
    )

    return (
        <ChipWrapper>
            {isOver && dropPosition && <DropIndicator position={dropPosition} />}
            <div
                ref={previewRef}
                data-attendee-id={validId}
                data-render-key={renderKey}
                style={{
                    opacity,
                    cursor: 'default',
                    display: 'inline-block',
                    pointerEvents: readonlyForCreate ? 'none' : 'auto'
                }}>
                <TransparentSelectChip
                key={key}
                data-testid={`Chip_${key}`}
                tabIndex={0}
                onKeyDown={handleChipContainerKeyDown}
                isOver={isOver}
                isDragging={isDragging}
                label={
                    <ChipLabelContainer>
                        {icon && (
                            <AtlasTooltip
                                title={i18n.t('ATTENDANCE_MOVE') || 'Move'}
                                placement="top">
                                <IconContainer
                                    ref={dragHandleRef}
                                    role="button"
                                    tabIndex={0}
                                    aria-label={i18n.t('ATTENDANCE_MOVE')}>
                                    {icon}
                                </IconContainer>
                            </AtlasTooltip>
                        )}
                        <LabelContainer
                            role="button"
                            tabIndex={0}
                            onClick={handleClick}
                            onTouchEnd={handleLabelTouchEnd}
                            onKeyDown={handleKeyDown}
                            aria-label={`Toggle between name and email. Currently showing: ${displayType}`}>
                            {label}
                        </LabelContainer>
                        {interactiveIcon && (
                            <AtlasTooltip
                                title={isTimeLate ? ATTENDANCE_PARTIAL_TEXT : ATTENDANCE_FULL_TEXT}
                                placement="top">
                                <InteractiveIconContainer
                                    key={`time-icon-${renderKey}-${isTimeLate}`}
                                    isActive={isTimeLate}
                                    role="button"
                                    tabIndex={0}
                                    onClick={handleTimeIconClick}
                                    onTouchEnd={handleTimeIconTouchEnd}
                                    onKeyDown={handleTimeIconKeyDown}
                                    aria-label={`Toggle time status. Currently: ${
                                        isTimeLate ? ATTENDANCE_PARTIAL_TEXT : ATTENDANCE_FULL_TEXT
                                    }`}>
                                    {interactiveIcon}
                                </InteractiveIconContainer>
                            </AtlasTooltip>
                        )}
                        {secondaryInteractiveIcon && (
                            <AtlasTooltip
                                title={isLocationRemote ? REMOTE_TEXT : IN_PERSON_TEXT}
                                placement="top">
                                <InteractiveIconContainer
                                    key={`location-icon-${renderKey}-${isLocationRemote}`}
                                    isActive={isLocationRemote}
                                    role="button"
                                    tabIndex={0}
                                    onClick={handleLocationIconClick}
                                    onTouchEnd={handleLocationIconTouchEnd}
                                    onKeyDown={handleLocationIconKeyDown}
                                    aria-label={`Toggle location status. Currently: ${
                                        isLocationRemote ? REMOTE_TEXT : IN_PERSON_TEXT
                                    }`}>
                                    {secondaryInteractiveIcon}
                                </InteractiveIconContainer>
                            </AtlasTooltip>
                        )}
                        {(interactiveIcon || secondaryInteractiveIcon) && <Divider />}
                    </ChipLabelContainer>
                }
                deleteIcon={
                    <AtlasTooltip title={i18n.t('ATTENDANCE_REMOVE') || 'Remove'} placement="top">
                        <DeleteIconContainer
                            role="button"
                            tabIndex={0}
                            onClick={handleDeleteClick}
                            onTouchEnd={handleDeleteTouchEnd}
                            onKeyDown={handleDeleteKeyDown}
                            aria-label={i18n.t('ATTENDANCE_REMOVE')}>
                            <StyledIconDelete />
                        </DeleteIconContainer>
                    </AtlasTooltip>
                }
                onDelete={(e) => {
                    // Prevent default chip delete behavior - we handle it in the icon handlers
                    e?.preventDefault?.()
                    e?.stopPropagation?.()
                }}
            />
            </div>
        </ChipWrapper>
    )
}

const InteractiveIconContainer = styled.span<{ isActive?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 2px;
    margin: 3px 2px;
    border-radius: 4px;
    transition: all 0.2s ease;
    min-width: 24px;
    min-height: 24px;
    box-sizing: border-box;
    border: 2px solid transparent;
    touch-action: manipulation;

    svg,
    svg path {
        fill: ${({ isActive }) =>
            isActive
                ? 'var(--Core-Color-Legacy-Black-Near, #1E1E1E)'
                : 'var(--Core-Color-Legacy-Light, #B3B3B3)'} !important;
        transition: fill 0.2s ease;
    }



    &:focus {
        outline: none !important;
        box-shadow: 0 0 0 2px ${ColorBaseSkyBlue} !important;
        border-radius: 4px;
    }



    &:focus-visible {
        outline: none !important;
        box-shadow: 0 0 0 2px ${ColorBaseSkyBlue} !important;
        border-radius: 4px;
    }

`

const IconContainer = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: grab;
    padding: 2px;
    margin: 3px 2px;
    border-radius: 4px;

    svg,
    svg path {
        fill: var(--Core-Color-Legacy-Light, #b3b3b3) !important;
    }

    &:hover svg,
    &:hover svg path {
        fill: var(--Core-Color-Legacy-Black-Near, #1e1e1e) !important;
    }

    &:focus {
        outline: none !important;
        box-shadow: 0 0 0 2px ${ColorBaseSkyBlue} !important;
        border-radius: 4px;
    }

    &:focus svg,
    &:focus svg path {
        fill: var(--Core-Color-Legacy-Black-Near, #1e1e1e) !important;
    }

    &:focus-visible {
        outline: none !important;
        box-shadow: 0 0 0 2px ${ColorBaseSkyBlue} !important;
        border-radius: 4px;
    }

    &:focus-visible svg,
    &:focus-visible svg path {
        fill: var(--Core-Color-Legacy-Black-Near, #1e1e1e) !important;
    }
`

const LabelContainer = styled.span`
    flex: 0 1 auto;
    cursor: pointer;
    padding-left: 6px;
    padding-right: 4px;
    margin-top: 3px;
    margin-bottom: 3px;
    color: var(--Semantic-Color-Type-Default, #1e1e1e);
    max-width: 335px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    touch-action: manipulation;

    &:focus {
        outline: none !important;
        box-shadow: 0 0 0 2px ${ColorBaseSkyBlue} !important;
        border-radius: 4px;
    }

    &:focus-visible {
        outline: none !important;
        box-shadow: 0 0 0 2px ${ColorBaseSkyBlue} !important;
        border-radius: 4px;
    }
`

const ChipLabelContainer = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
`

const Divider = styled.div`
    width: 0;
    min-width: 0;
    max-width: 0;
    height: 24px;
    border-left: 1px solid #676767;
    margin: 0;
    flex-shrink: 0;
    transform: translateZ(0);
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
`

const DeleteIconContainer = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    outline: none;
    padding: 2px;
    margin: 0;
    border-radius: 4px;
    min-width: 24px;
    min-height: 24px;

    &:focus {
        outline: none !important;
        box-shadow: 0 0 0 2px ${ColorBaseSkyBlue} !important;
        border-radius: 4px;
    }

    &:focus-visible {
        outline: none !important;
        box-shadow: 0 0 0 2px ${ColorBaseSkyBlue} !important;
        border-radius: 4px;
    }
`

const StyledIconDelete = styled(IconDeleteDateRow)`
    width: 16px;
    height: 16px;
    cursor: pointer;

    & path {
        fill: var(--Core-Color-Legacy-Light, #b3b3b3) !important;
    }

    &:hover path {
        fill: var(--Core-Color-Legacy-Black-Near, #1e1e1e) !important;
    }
`
